import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useTranslation } from 'react-i18next';

interface TestCaseFeedbackDialogProps {
  open: boolean;
  submitting: boolean;
  appliesToCurrentVersion?: boolean;
  onClose: () => void;
  onSubmit: (instruction: string) => Promise<void> | void;
}

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    paddingRight: theme.spacing(1),
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  description: {
    marginBottom: theme.spacing(2),
  },
  closeButton: {
    minWidth: 96,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.grey[700],

    '&:hover': {
      backgroundColor: theme.palette.grey[800],
    },

    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      backgroundColor: theme.palette.action.disabledBackground,
    },
  },
}));

export const TestCaseFeedbackDialog = ({
  open,
  submitting,
  appliesToCurrentVersion = false,
  onClose,
  onSubmit,
}: TestCaseFeedbackDialogProps) => {
  const classes = useStyles();
  const { t } = useTranslation('testCases');

  const [instruction, setInstruction] = useState('');

  useEffect(() => {
    if (open) {
      setInstruction('');
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    const trimmedInstruction = instruction.trim();

    if (!trimmedInstruction || submitting) {
      return;
    }

    await onSubmit(trimmedInstruction);
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.titleContent}>
          <Typography variant="h6">
            {t('testCaseGeneration.feedback.dialogTitle')}
          </Typography>

          <IconButton
            size="small"
            aria-label={t('testCaseGeneration.feedback.cancel')}
            disabled={submitting}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.description}
        >
          {appliesToCurrentVersion
            ? t('testCaseGeneration.feedback.currentVersionDescription')
            : t('testCaseGeneration.feedback.dialogDescription')}
        </Typography>

        <TextField
          multiline
          rows={4}
          fullWidth
          variant="outlined"
          label={t('testCaseGeneration.feedback.instructionLabel')}
          placeholder={t('testCaseGeneration.feedback.instructionPlaceholder')}
          value={instruction}
          disabled={submitting}
          onChange={event => setInstruction(event.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button
          className={classes.closeButton}
          variant="contained"
          disabled={submitting}
          onClick={handleClose}
        >
          {t('testCaseGeneration.feedback.cancel')}
        </Button>

        <Button
          color="primary"
          variant="contained"
          disabled={!instruction.trim() || submitting}
          onClick={handleSubmit}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {submitting
            ? t('testCaseGeneration.feedback.generatingRevision')
            : t('testCaseGeneration.feedback.generateRevision')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestCaseFeedbackDialog;
